/**
 * Safe YYYY-MM-DD date parsing and formatting helpers.
 *
 * The built-in `new Date("YYYY-MM-DD")` interprets the string as UTC midnight,
 * which can shift the displayed day when formatted in a local timezone that is
 * behind UTC (e.g. US timezones).  These helpers treat "YYYY-MM-DD" as a
 * **date-only** value and avoid timezone drift by parsing the components
 * manually and constructing a Date set to local midnight.
 *
 * The module is dependency-free and safe for both server and browser.
 */

// --------------------------------------------------------------------------
// Regex for strict YYYY-MM-DD validation
// --------------------------------------------------------------------------

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

// --------------------------------------------------------------------------
// Parsing
// --------------------------------------------------------------------------

/**
 * Parse a `YYYY-MM-DD` string into a `Date` set to **local midnight**.
 *
 * Unlike `new Date("2025-01-15")` which is interpreted as UTC midnight
 * (and therefore Dec 14 in US timezones), this function creates a Date
 * anchored to the local timezone so that `.toLocaleDateString()` always
 * returns the expected calendar day.
 *
 * Throws if the string does not match `YYYY-MM-DD`.
 */
export function parseLocalDate(dateStr: string): Date {
  const match = DATE_RE.exec(dateStr);
  if (!match) {
    throw new RangeError(
      `parseLocalDate: expected YYYY-MM-DD, got "${dateStr}"`,
    );
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1; // JS months are 0-based
  const day = Number(match[3]);
  const date = new Date(year, month, day);

  // Guard against silent rollover: new Date(2025, 1, 31) becomes Mar 3.
  // If the resulting date's components differ from the input, the calendar
  // date was invalid.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    throw new RangeError(
      `parseLocalDate: invalid calendar date "${dateStr}"`,
    );
  }

  return date;
}

// --------------------------------------------------------------------------
// Formatting
// --------------------------------------------------------------------------

/** Default Intl options: "Dec 15, 2025" */
const SHORT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

/**
 * Format a `YYYY-MM-DD` string to a human-readable display string.
 *
 * Example: `"2025-12-15"` -> `"Dec 15, 2025"`
 */
export function formatLocalDateDisplay(
  dateStr: string,
  locale = "en-US",
  options: Intl.DateTimeFormatOptions = SHORT_DATE_OPTS,
): string {
  return parseLocalDate(dateStr).toLocaleDateString(locale, options);
}

/**
 * Format a date range from two `YYYY-MM-DD` strings for display.
 *
 * Example: `("2025-11-15", "2025-12-15")` -> `"Nov 15, 2025 – Dec 15, 2025"`
 */
export function formatLocalDateRangeDisplay(start: string, end: string): string {
  return `${formatLocalDateDisplay(start)} – ${formatLocalDateDisplay(end)}`;
}

// --------------------------------------------------------------------------
// YYYY-MM-DD serialisation
// --------------------------------------------------------------------------

/**
 * Format a `Date` object to a `YYYY-MM-DD` string using **local** getters.
 *
 * This is the timezone-safe replacement for
 * `date.toISOString().split("T")[0]`, which uses UTC getters and can shift
 * the day in behind-UTC timezones.
 */
export function formatLocalDateYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Extract the `YYYY-MM-DD` portion from either a plain date string or a
 * full ISO-8601 datetime string.  Returns the input unchanged if it is
 * already in `YYYY-MM-DD` form.
 *
 * This is a pure string operation — no Date object is created.
 */
export function toDateOnly(input: string): string {
  // Already YYYY-MM-DD
  if (DATE_RE.test(input)) return input;
  // ISO datetime: take first 10 chars
  if (input.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(input)) {
    return input.slice(0, 10);
  }
  throw new RangeError(
    `toDateOnly: cannot extract date from "${input}"`,
  );
}
